/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { common, Portals } from '@qwyk/models';
import { ShipmentSubscriptionResponse } from '@qwyk/ui-standalone';
import { ShipmentPackage } from '@qwyk/shared-stores/shipment-packages';
import { ShipmentAddress } from '@qwyk/shared-stores/shipment-addresses';
import { ShipmentAttachment } from '@qwyk/shared-stores/shipment-attachments';
import { ShipmentTimelineEvent } from '@qwyk/shared-stores/shipment-timeline-events';

export type ShipmentState = common.ShipmentState;

export interface ShipmentUI extends Shipment {
    transport_mode_product: string;
}

export interface Shipment {
    id: string;
    organization_shipment_reference: string;
    name: string;
    owner: User;
    booked_at: Date;
    last_event?: ShipmentTimelineEvent;
    next_event?: ShipmentTimelineEvent;
    data: ShipmentData;
    sys_state: ShipmentState;
    is_master: boolean;
    house_shipments_count: number;
    has_internal_locode?: boolean;
    status: string;
    created_on: Date;
    carrier_name: string;
    intermediate_consignee_name: string;
    executed_place?: null | string;
    booking_number: string;
    issued_by_name: string;
    destination_agent_name: string;
    direction_text: string;
    notes: string;
    delivery_date: Date;
    received_by_name: string;
    pod_comments: string;
    billing_client_name: string;
    entry_number?: null;
    cut_off_date: Date;
    spotting_date: Date;
    insurance_number: string;
    insurance_date: Date;
    declared_value: string;
    declared_value_for_customs?: null;
    has_attachments: boolean;
    custom_fields_dictionary?: any;
    subscription: ShipmentSubscriptionResponse;
    attachments: ShipmentAttachment[];
    created_at: Date;
    updated_at: Date;
    deleted_at?: Date;
    operator?: any; // TODO: fix
    tracking_request_id?: string;
    tracking_request_status?: string;
    tracking_request_failed_reason?: string;
    tracking_info?: ShipmentTrackingInfo;
    tracking_request_cancellation_failed_at: Date;
    warnings?: string[];
    purchase_orders: ShipmentPurchaseOrder[];
    legs?: ShipmentLeg[];
    tags?: common.CustomTag[];
}

export interface ShipmentLeg {
    arrival_actual: string | null;
    arrival_estimated: string | null;
    arrival_port: ShipmentLegLocation;
    carrier: string | null;
    carrier_detailed: {
        booking_url: string | null;
        is_official_scac: boolean;
        is_terminal49_enabled: boolean;
        name: string | null;
        qwykschedules_code: string | null;
        scac: string | null;
    } | null;
    carrier_scac: string | null;
    departure_actual: string | null;
    departure_estimated: string | null;
    destination: {
        waypoint_locode: ShipmentLegLocation | null;
        waypoint_locode_description: string | null;
    };
    imo_number: string | null;
    lading_port: ShipmentLegLocation;
    leg_sequence_number: number;
    leg_type: 'Other' | 'Main' | 'PreCarriage' | 'OnCarriage';
    origin: {
        waypoint_locode: ShipmentLegLocation | null;
        waypoint_locode_description: string | null;
    };
    transport_mode: string | null;
    vessel_name: string | null;
    vessel_type_code: string | null;
    voyage_number: string | null;
}

export interface ShipmentLegLocation {
    country_code: string;
    country_name: string;
    display_name: string;
    locode: string;
    name: string;
    position: Position | null;
    timezone: string | null;
}

export interface Position {
    type: string;
    coordinates: [number, number];
}

export interface User {
    id: string;
    name: string;
    email: string;

    username: string;
    team_id: string;
    team_name: string;
    phone_number: string;
    job_title: string;
    company_name: string;
    location: string;
    country: any;
    country_code: string;
    registered_site_id: number;
    restrict_to_registered_site: boolean;
    marketing_optin: boolean;
    email_verified_at: Date;
    email_verified: boolean;
    approved_at: Date;
    approved: boolean;
    approved_by: string;
    last_login_at: Date;
    created_at: Date;
    deactivated: boolean;
}

export interface ShipmentTrackingInfo {
    provider: null | string;
    request_id: null | string;
    shipment_id: null | string;
    status: null | string;
    url: null | string;
}

export interface ShipmentData {
    id: number;
    transport_mode: string;
    product: string;
    mbl_number?: string;
    hbl_number?: string;
    vessel: string;
    voyage: string;
    place_of_receipt?: ShipmentDataLocation;
    place_of_receipt_locode?: string;
    port_of_loading?: ShipmentDataLocation;
    port_of_loading_locode: string;
    port_of_discharge_locode: string;
    port_of_discharge?: ShipmentDataLocation;
    place_of_delivery?: ShipmentDataLocation;
    place_of_delivery_locode?: string;
    final_destination?: any;
    shipper: ShipmentAddress;
    consignee: ShipmentAddress;
    notify: ShipmentAddress;
    pickup: ShipmentAddress;
    delivery: ShipmentAddress;
    containers: any[];
    packages: ShipmentPackage[];
    service_type?: string;
    etd?: string | Date;
    etd_tzn?: string;
    etd_local?: string | Date;
    eta?: string | Date;
    eta_tzn?: string;
    eta_local?: string | Date;
    cargo_ready?: string | Date;
    cargo_ready_tzn?: string;
    carrier?: ShipmentDataCarrier;
    shipper_reference?: string;
    consignee_reference?: string;
    forwarder_reference?: string;
    forwarder_reference_notes?: string;
    pricing_reference?: string;
    notify_reference?: string;
    inco_term?: string;
    inco_term_place?: string;
    payment_terms?: string;
    remarks?: string;
    total_weight?: Portals.MultiUnitValue;
    total_volume?: Portals.MultiUnitValue;
    total_pieces?: number;
    schedule_readonly: boolean;
    pricing_reference_readonly: boolean;
    customer_party_type?: string;
    vessel_imo?: string;
    vessel_mmsi?: string;
    shipper_name: string;
    consignee_name: string;
    total_volume_weight: Portals.MultiUnitValue;
    notify_name: string;
    created_by_name: string;
    description: string;
    point_of_origin_ftz_number: string;
    loading_pier_or_terminal: string;
    pre_carriage_by: string;
    place_of_receipt_by_pre_carrier?: string;
    total_containers: number;
    external_tracking_numbers?: string;
    containers_numbers: string;
    us_customs_destination: string;
    commodities_description: string;
    purchase_order_numbers: string;
    vin_number?: string;
    is_transhipment?: boolean;
    us_customs_origin?: string;
}

export interface ShipmentDataCarrier {
    scac: string;
    name: string;
    qwykschedules_code?: string;
    is_official_scac: boolean;
    booking_url?: string;
    is_terminal49_enabled: boolean;
}

export interface ShipmentDataLocation {
    locode: string;
    name: string;
    display_name: string;
    country_code: string;
    country_name: string;
    position?: [number, number] | Position;
}

export interface ShipmentTrackingRequest {
    carrier_scac: string;
    mbl_number: string;
}

export interface ShipmentTrackingRequestResponse {
    tracking_request_status: string;
    tracking_request_id?: string;
    tracking_request_failed_reason?: string;
}

export interface ShipmentPurchaseOrder {
    id: string;
    po_number: string;
    buyer: string;
    supplier: string;
}

export interface SearchQuery {
    full?: string;
    name?: string;
    organization_shipment_reference?: string;
    hbl_number?: string;
    mbl_number?: string;
    current_milestone?: string;
    eta_range?: [string, string];
    vessel?: string;
    container_number?: string;
    shipper?: string;
    consignee?: string;
    place_of_receipt?: { locode: string };
    place_of_delivery?: { locode: string };
    port_of_loading?: { locode: string };
    port_of_discharge?: { locode: string };
    product_transport_mode?: string;
    team_id?: string[];
    order_by?: ShipmentSortField;
    order_by_direction?: SortDirection;
    tags?: string[];
    is_subscribed?: boolean[] | number[];
}

export interface BackendQuery {
    sys_state: string
}

export const SHIPMENT_SORT_FIELDS = {
    UPDATED_AT: 'updated_at',
    BOOKED_AT: 'booked_at',
    ETD: 'data.etd',
    ETA: 'data.eta'
} as const;

export type ShipmentSortField = typeof SHIPMENT_SORT_FIELDS[keyof typeof SHIPMENT_SORT_FIELDS];

export type SortDirection = 'asc' | 'desc';


